import { Component, inject, Input, OnChanges, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import {
  getComcastChartColorByIndex,
  getComcastChartPalette,
} from 'src/app/shared/constants/chart-colors';
import { getServicesLoading, State } from 'src/app/store';

@Component({
  selector: 'app-service-type-chart',
  templateUrl: './service-type-chart.component.html',
  standalone: false,
})
export class ServiceTypeChartComponent implements OnChanges {
  private readonly store = inject(Store<State>);

  @Input() data: { [key: string]: number };

  readonly loading = toSignal(this.store.select(getServicesLoading));

  @ViewChild(BaseChartDirective) chart: BaseChartDirective;

  hiddenLabels = new Set<string>();

  private sourceData: { label: string; value: number }[] = [];

  public pieChartType: ChartType = 'doughnut' as ChartType;

  public pieChartOptions: ChartConfiguration<'doughnut'>['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '62%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(17, 24, 39, 0.96)',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        titleFont: {
          family: 'Montserrat',
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          family: 'Lato',
          size: 13,
          weight: 'normal',
        },
        padding: { top: 12, right: 14, bottom: 12, left: 14 },
        caretPadding: 8,
        caretSize: 8,
        cornerRadius: 8,
        boxPadding: 6,
        bodySpacing: 6,
        titleMarginBottom: 8,
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          label: (context) => `Services: ${context.parsed}`,
          labelPointStyle: () => ({ pointStyle: 'rectRounded', rotation: 0 }),
        },
      },
    },
    layout: {
      padding: 4,
    },
  };

  public pieChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Total',
        borderWidth: 0,
        hoverBorderWidth: 0,
        backgroundColor: getComcastChartPalette(),
      },
    ],
  };

  ngOnChanges(): void {
    this.setChartData();
  }

  get legendItems(): { label: string; color: string; isHidden: boolean }[] {
    const labels = (this.pieChartData.labels as string[]) ?? [];
    const colors =
      (this.pieChartData.datasets[0].backgroundColor as string[]) ?? [];
    return labels.map((label, i) => ({
      label,
      color: colors[i] ?? getComcastChartColorByIndex(i),
      isHidden: this.hiddenLabels.has(label),
    }));
  }

  toggleLegendItem(label: string): void {
    if (this.hiddenLabels.has(label)) {
      this.hiddenLabels.delete(label);
    } else {
      this.hiddenLabels.add(label);
    }
    this.applyChartData();
  }

  private setChartData(): void {
    const entries = Object.entries(this.data ?? {});

    entries.sort((a, b) => a[0].localeCompare(b[0]));

    this.sourceData = entries.map(([label, value]) => ({ label, value }));

    const newLabels = new Set(this.sourceData.map((d) => d.label));
    Array.from(this.hiddenLabels)
      .filter((hidden) => !newLabels.has(hidden))
      .forEach((hidden) => this.hiddenLabels.delete(hidden));

    this.applyChartData();
  }

  private applyChartData(): void {
    this.pieChartData.labels = this.sourceData.map(
      (serviceEntry) => serviceEntry.label,
    );
    this.pieChartData.datasets[0].data = this.sourceData.map((serviceEntry) =>
      this.hiddenLabels.has(serviceEntry.label) ? 0 : serviceEntry.value,
    );
    const palette = getComcastChartPalette();
    this.pieChartData.datasets[0].backgroundColor = this.sourceData.map(
      (_, index) => getComcastChartColorByIndex(index, palette),
    );
    this.chart?.update();
  }
}
